import { env } from "./env";
import { resolveKeycloakClientId, resolveKeycloakRealm, resolveKeycloakUrl } from "./runtimeConfig";

const getDefaultRedirectUri = (pathname: string) => {
  if (typeof window === "undefined") {
    return "";
  }

  // Normalize 127.0.0.1 → localhost so the redirect_uri always matches
  // the Keycloak client's allowed redirect URIs (which use "localhost").
  const origin = window.location.origin.replace("://127.0.0.1", "://localhost");

  return `${origin}${pathname}`;
};

/**
 * Deployed builds always use the current origin so one image can serve every
 * stage (DEV/QA/preprod/prod). Local `npm run dev` may still override via
 * VITE_KEYCLOAK_*_REDIRECT_URI for odd port setups.
 */
const resolveRedirectUri = (pathname: string, envOverride: string | undefined) => {
  if (import.meta.env.DEV && envOverride) {
    return envOverride;
  }

  return getDefaultRedirectUri(pathname);
};

export const keycloakConfig = {
  get url() {
    return resolveKeycloakUrl();
  },
  get realm() {
    return resolveKeycloakRealm();
  },
  get clientId() {
    return resolveKeycloakClientId();
  },
  get redirectUri() {
    return resolveRedirectUri("/auth/callback", env.VITE_KEYCLOAK_REDIRECT_URI);
  },
  get logoutRedirectUri() {
    return resolveRedirectUri("/", env.VITE_KEYCLOAK_LOGOUT_REDIRECT_URI);
  },
};

/**
 * OIDC endpoint base paths.
 *
 * Browser redirects (authorization, logout) use the full Keycloak URL so the
 * browser navigates to the real Keycloak host.
 *
 * Fetch-based calls (token, userInfo) use a relative path so they go through
 * the Vite dev proxy and avoid CORS issues. In production builds the full URL
 * is used since there is no dev proxy.
 *
 * Paths are getters so they pick up per-stage values from `/runtime-config.js`.
 */
export const keycloakEndpoints = {
  /** Browser redirect — full URL */
  get authorization() {
    return `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth`;
  },
  /** fetch() call — proxied in dev */
  get token() {
    const oidcPath = `/realms/${keycloakConfig.realm}/protocol/openid-connect`;
    return import.meta.env.DEV ? `${oidcPath}/token` : `${keycloakConfig.url}${oidcPath}/token`;
  },
  /** fetch() call — proxied in dev */
  get userInfo() {
    const oidcPath = `/realms/${keycloakConfig.realm}/protocol/openid-connect`;
    return import.meta.env.DEV
      ? `${oidcPath}/userinfo`
      : `${keycloakConfig.url}${oidcPath}/userinfo`;
  },
  /** Browser redirect — full URL */
  get logout() {
    return `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/logout`;
  },
};
